import React from 'react';
import {
	Breakpoints,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Layout,
	List,
	ListGutter,
	ListItem,
	Text,
	TokenTextAppearance,
	ThemeProvider,
} from '@volkswagen-onehub/components-core';
import './TechnicalDetails.scss';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { LayerHandleV2 } from '@volkswagen-onehub/layer-manager';
import { Modal } from '../../models/Modal';
import { TechnicalData, TechnicalDataObject } from '../../models/TechnicalData';
import { DataRequestState } from '../../models/DataRequestState';
import { VehicleObject } from '../../models/Vehicle';

export default function TechnicalDetails({ close, state }: Modal<TechnicalDetailsProps>) {
	const { technicalData, vehicle, trackingService, isLoggedIn, onClickDisclaimer, footnotes, t } = state;

	const vinCheckTrackingVehicleLayerLoad = () => {
		if (trackingService) {
			trackingService.track('VWBasic_VehicleLayer_Load', {
				eventInfo: {
					eventAction: 'VWBasic_VehicleLayer_Load',
					pageId: window.location.href,
					pageName: 'Vin Details',
					contentId: 'vinDetails.technicalDetails',
					sectionName: 'technicalDetails',
					brand: 'passenger',
				},
				user: {
					loginStatus: isLoggedIn,
					browserResolutionHeight: window.innerHeight,
					browserResolutionWidth: window.innerWidth,
					browserResolutionOrientation: window.innerHeight >= window.innerWidth ? 'v' : 'h',
				},
				configuration: {
					CarlineName: vehicle.data.modelCarLine,
					SalesgroupName: vehicle.data.modelSalesGroup,
					ModelYear: vehicle.data.modelYear,
					GearType: technicalData.data.engineGearboxElectrics?.gearbox || undefined,
				},
			});
		}
	};

	const isDataAvailable = (obj: any) => {
		return obj && Object.keys(obj).length !== 0;
	};

	const getDisclaimerButton = (showERangeDisclaimer: boolean) => {
		if (showERangeDisclaimer) {
			return (
				<button type="button" className="disclaimer-button-technical-details" onClick={() => onClickDisclaimer(1)}>
					{footnotes[1].reference}
				</button>
			);
		}
		return <></>;
	};

	const getListEntry = (
		property:
			'preFilledAuthData' | 'consumptionNEDC' | 'consumptionWLTP' | 'outsideDimensions' | 'engineGearboxElectrics' | 'weights' | 'furtherInformation',
		entry: [string, any],
	) => {
		const showERangeDisclaimer: boolean = entry[0] === 'rangeElectric' || entry[0] === 'rangeElectricUrban' || entry[0] === 'rangeElectricOutOfTown';
		return (
			<ListItem appearance={TokenTextAppearance.label200}>
				{t(`technicalDetails.${property}.${entry[0]}`)}: {entry[1]}&nbsp;{getDisclaimerButton(showERangeDisclaimer)}
			</ListItem>
		);
	};

	const TechnicalDetail = (property: keyof TechnicalData) => (
		<Container wrap="always" gutter={ContainerGutter.dynamic0050}>
			{technicalData.state === DataRequestState.Available ? (
				<Text appearance={TokenTextAppearance.headline200} bold>
					{t(`technicalDetails.${property}.title`)}
				</Text>
			) : (
				<Skeleton height={24} />
			)}
			<List gutter={ListGutter.static150}>
				{}
				{Object.entries(technicalData.data[property]!).map((entry) =>
					// TODO 16112021
					// eslint-disable-next-line no-nested-ternary
					entry[1] ? getListEntry(property, entry) : technicalData.state !== DataRequestState.Available ? <Skeleton /> : <></>,
				)}
			</List>
		</Container>
	);

	vinCheckTrackingVehicleLayerLoad();

	return (
		<ThemeProvider theme="main">
			<Container wrap="always" gutter={ContainerGutter.dynamic0150} padding={{ left: ContainerPadding.grid002, right: ContainerPadding.grid002 }}>
				<div className="technical-details-title">
					<Text appearance={TokenTextAppearance.headline600}>{t('technicalDetails.technicalDataHeader')}</Text>
				</div>

				<Layout
					appearance={{
						[Breakpoints.b960]: [
							{ name: 'a', columns: 11 },
							{ name: '.', columns: 2 },
							{ name: 'b', columns: 11 },
						],
						[Breakpoints.default]: [
							{ name: 'a', columns: 24 },
							{ name: 'b', columns: 24 },
						],
					}}
				>
					<Container wrap="always" gutter={ContainerGutter.dynamic0150} padding={{ bottom: ContainerPadding.dynamic0100 }}>
						{isDataAvailable(technicalData.data.engineGearboxElectrics) ? TechnicalDetail('engineGearboxElectrics') : <></>}
						{isDataAvailable(technicalData.data.outsideDimensions) ? TechnicalDetail('outsideDimensions') : <></>}
						{isDataAvailable(technicalData.data.weights) ? TechnicalDetail('weights') : <></>}
					</Container>
					<Container wrap="always" gutter={ContainerGutter.dynamic0150}>
						{isDataAvailable(technicalData.data.consumptionWLTP) ? TechnicalDetail('consumptionWLTP') : <></>}
						{isDataAvailable(technicalData.data.consumptionNEDC) ? TechnicalDetail('consumptionNEDC') : <></>}
					</Container>
				</Layout>

				<CTA tag="button" emphasis="secondary" onClick={close} stretchContent={{ [Breakpoints.default]: true, [Breakpoints.b560]: false }}>
					{t('general.close')}
				</CTA>
			</Container>
		</ThemeProvider>
	);
}

export interface TechnicalDetailsProps {
	technicalData: TechnicalDataObject;
	vehicle: VehicleObject;
	trackingService: any;
	isLoggedIn: boolean;
	onClickDisclaimer: (disclaimerNumber: number) => void;
	footnotes: { reference: string; text: string; handle: LayerHandleV2<any> }[];
	tracking: {
		contentId: string;
		sectionName: string;
	};
	t: any;
}
